import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, X, Play, Pause, RotateCcw,
  CheckCircle2, Volume2, VolumeX, Maximize2, Minimize2,
  MousePointer2, Hand, Keyboard, Eye, Target, Info
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';

// Interactive Tutorial System - Shows REAL KEY-MADE interface with animated cursor
// This creates video-like walkthroughs using actual screenshots and animations

// Tutorial Step Configuration
// Each step defines:
// - screenshot: The actual KEY-MADE screenshot to show
// - cursorPath: Array of {x, y} coordinates for cursor animation
// - clickTarget: Final click position with pulsing indicator
// - narration: Detailed explanation of what's happening
// - typingDemo: Optional text to show being typed
// - highlightAreas: Areas to highlight on the screenshot

const INTERACTIVE_TUTORIALS = {
  'welcome-dashboard': {
    title: 'Mastering Your Dashboard',
    description: 'Learn to navigate KEY-MADE like a pro',
    duration: '8 minutes',
    steps: [
      {
        id: 'welcome-1',
        title: 'Welcome to Your Command Center',
        screenshot: '/tutorials/welcome/01-dashboard-overview.png',
        narration: `This is **My Desk** - your personal command center in KEY-MADE.

Everything you need to manage your architectural and interior design projects is organized right here. Let's explore what you're seeing:

**The Dashboard Layout:**
• **Left sidebar** - Your main navigation to all KEY-MADE features
• **Center area** - Today's Focus with events, tasks, and project blocks
• **Right section** - Calendar view with weather integration

**Key Features You'll See:**
• **Today's Focus** - Your current priorities at a glance
• **Active Projects** - Quick-access cards to jump into any project
• **Calendar Widget** - Weekly view with Google Calendar sync
• **Time Tracker** - Built-in time tracking for billing

As a designer, you'll start each workday here, checking your schedule, reviewing priorities, and planning your activities.`,
        highlightAreas: [
          { x: 10, y: 60, width: 150, height: 700, label: 'Navigation Sidebar' },
          { x: 180, y: 100, width: 600, height: 400, label: 'Today\'s Focus' },
          { x: 180, y: 550, width: 800, height: 250, label: 'Calendar Widget' }
        ],
        duration: 10000,
      },
      {
        id: 'welcome-2',
        title: 'The Navigation Sidebar',
        screenshot: '/tutorials/welcome/01-dashboard-overview.png',
        narration: `The **Navigation Sidebar** is your quick access to every KEY-MADE feature.

**Main Sections:**

📋 **CORE NAVIGATION:**
• **My Desk** - Return to your dashboard anytime
• **Projects** - View all your projects
• **Tasks** - Manage to-do items
• **Calendar** - Full calendar view
• **Time Tracking** - Log billable hours
• **Notes** - Quick notes and memos

🛠️ **TOOLS:**
• **Leads / CRM** - Manage potential clients
• **Clip Library** - Your product database
• **Contacts** - All your contacts
• **Contractors** - GCs and subcontractors
• **Documents** - Templates and files
• **Blueprint Library** - Help guides like this one!

⚙️ **ADMIN TOOLS (Admins Only):**
• **Back of House** - Financial dashboard
• **Company Settings** - Branding and preferences
• **Admin Settings** - System configuration

**Pro Tip:** Click the collapse button to minimize the sidebar when you need more workspace.`,
        cursorPath: [
          { x: 80, y: 75, delay: 0 },
          { x: 80, y: 150, delay: 500 },
          { x: 80, y: 250, delay: 1000 },
          { x: 80, y: 400, delay: 1500 },
          { x: 80, y: 550, delay: 2000 },
        ],
        highlightAreas: [
          { x: 10, y: 60, width: 150, height: 700, label: 'Navigation Menu' }
        ],
        duration: 12000,
      },
      {
        id: 'welcome-3',
        title: 'Project Summary Cards',
        screenshot: '/tutorials/welcome/01-dashboard-overview.png',
        narration: `The **Project Summary Cards** give you instant visibility into your active projects.

**What Each Card Shows:**
• **Active Projects** - Total number of projects you're working on
• **Pending Tasks** - Tasks requiring your attention
• **Overdue Items** - Projects or tasks past their due date
• **New Leads** - Recent inquiries or potential projects

**Why This Matters:**
At a glance, you can see:
• How many projects need attention today
• Which tasks are falling behind
• Whether you have new business opportunities

**Clicking a Card:**
Each summary card is clickable! Click on:
• "Active Projects" to see your project list
• "Pending Tasks" to jump to your task manager
• "Overdue" to prioritize catch-up items
• "New Leads" to follow up on inquiries

**Best Practice:** Check these cards first thing every morning to prioritize your day.`,
        highlightAreas: [
          { x: 185, y: 445, width: 220, height: 90, label: 'Active Projects' },
          { x: 420, y: 445, width: 220, height: 90, label: 'Pending Tasks' },
          { x: 655, y: 445, width: 220, height: 90, label: 'Overdue' },
          { x: 890, y: 445, width: 220, height: 90, label: 'New Leads' }
        ],
        duration: 10000,
      },
      {
        id: 'welcome-4',
        title: 'The Weekly Calendar',
        screenshot: '/tutorials/welcome/01-dashboard-overview.png',
        narration: `The **Weekly Calendar** keeps your schedule front and center.

**Calendar Features:**
• **7-Day View** - See your entire week at a glance
• **Weather Integration** - Daily weather forecasts for planning site visits
• **Google Sync** - Connects to your Google Calendar
• **Event Colors** - Different colors for different event types

**Event Types You'll See:**
📅 **Meetings** - Client consultations, team meetings
🏗️ **Site Visits** - On-site inspections and walkthroughs
📝 **Tasks** - Due dates for your action items
🎨 **Design Reviews** - Presentation and approval sessions

**Adding Events:**
• Click on any day to add a new event
• Events sync automatically with Google Calendar
• Set reminders to never miss a deadline

**Navigation:**
Use the arrows to move between weeks, or click "View Full Calendar" for a complete calendar experience.

**Pro Tip:** The weather forecast helps you plan site visits on good weather days!`,
        highlightAreas: [
          { x: 185, y: 550, width: 800, height: 250, label: 'Weekly Calendar' }
        ],
        cursorPath: [
          { x: 300, y: 650, delay: 0 },
          { x: 500, y: 650, delay: 1000 },
          { x: 700, y: 650, delay: 2000 },
        ],
        duration: 12000,
      },
    ]
  },
  
  'creating-projects': {
    title: 'Creating & Managing Projects',
    description: 'Set up projects that organize your workflow',
    duration: '15 minutes',
    steps: [
      {
        id: 'project-1',
        title: 'Starting a New Project',
        screenshot: '/tutorials/welcome/01-dashboard-overview.png',
        narration: `Let's create a new project from scratch.

**Step 1: Click "Projects" in the Sidebar**
Navigate to the Projects section to see all your projects and create new ones.

**What You Need Before Starting:**
Have this information ready:
• **Client name** and contact information
• **Project address** (where the work will be done)
• **Project type** (Residential, Commercial, Hospitality, etc.)
• **Budget range** (helps with estimate comparisons)
• **Timeline expectations** (start date, deadlines)

**Project Naming Best Practices:**
Use a consistent format like:
• "LastName - Project Type" (e.g., "Smith - Kitchen Remodel")
• "Address - Scope" (e.g., "123 Main St - Full Renovation")
• "Client Company - Building" (for commercial projects)

**Why Naming Matters:**
Good naming conventions make it easy to:
• Search and filter your project list
• Identify projects at a glance
• Keep communications clear with clients and contractors`,
        cursorPath: [
          { x: 80, y: 105, delay: 0 },
        ],
        clickTarget: { x: 80, y: 105, label: 'Click "Projects"' },
        duration: 12000,
      },
      {
        id: 'project-2',
        title: 'The Project Hub Overview',
        screenshot: '/tutorials/welcome/01-dashboard-overview.png',
        narration: `After creating a project, you enter its **Project Hub** - the command center for that specific project.

**The Project Hub Layout:**

📊 **Header Section:**
• Project name and status badge
• Client contact information
• Quick action buttons

📁 **Overview Tab:**
• Project summary cards (Status, Items, Budget, Team)
• Client information and site address
• Project scope details
• Notes section

🗺️ **Management Tabs:**
• **Journey** - Track project phases and milestones
• **Phases** - Design phases (PreDesign, Schematic, etc.)
• **Actions** - Task and action item list
• **Progress** - Project completion tracking
• **Team** - Assign team members and contractors

🎨 **Design Tabs:**
• **Clips** - Products assigned to this project
• **Moods** - Mood boards for client presentations
• **Plans** - Floor plans and drawings

💰 **Financials Tabs:**
• **Estimates** - Cost proposals and pricing
• **Procurement** - RFPs and Purchase Orders
• **Invoices** - Client billing
• **Change Orders** - Scope modifications`,
        highlightAreas: [
          { x: 160, y: 215, width: 120, height: 600, label: 'Project Hub Tabs' }
        ],
        duration: 15000,
      },
      {
        id: 'project-3',
        title: 'Project Phases & Journey',
        screenshot: '/tutorials/welcome/01-dashboard-overview.png',
        narration: `The **Journey** feature tracks your project through professional design phases.

**Standard Design Phases:**

1️⃣ **Client Onboarding** (10%)
   • Initial consultation
   • Contract signing
   • Deposit received

2️⃣ **PreDesign** (10%)
   • Site survey
   • Client questionnaire
   • Research and inspiration

3️⃣ **Schematic Design** (15%)
   • Concept development
   • Space planning
   • Initial presentations

4️⃣ **Design Development** (25%)
   • Detailed drawings
   • Material selections
   • Client approvals

5️⃣ **Contract Negotiation** (10%)
   • Contractor bidding
   • Pricing finalization
   • Contract execution

6️⃣ **Construction Administration** (25%)
   • Site visits
   • Change orders
   • Quality control

7️⃣ **Project Closeout** (5%)
   • Punch list
   • Final walkthrough
   • Project documentation

**Each phase contains steps** that guide you through the process. Complete steps to advance your progress automatically.`,
        duration: 15000,
      },
    ]
  },

  'clips-mastery': {
    title: 'Mastering the Clip Library',
    description: 'Collect, organize, and use product clips effectively',
    duration: '15 minutes',
    steps: [
      {
        id: 'clips-1',
        title: 'What Are Clips?',
        screenshot: '/tutorials/clips/01-clips-library.png',
        narration: `**Clips** are the heart of KEY-MADE's product management system.

**Think of clips as digital product samples** - each one represents a furniture piece, fixture, material, or finish you might specify for a project.

**What a Clip Contains:**
📷 **Product Image** - Visual reference from the vendor
💲 **Pricing** - Current cost information
🏭 **Vendor Information** - Where to purchase
📐 **Specifications** - Dimensions, materials, finishes
🏠 **Room Assignment** - Where it goes in the project
📝 **Your Notes** - Why you selected it, alternatives, etc.
📄 **Spec Sheets** - Manufacturer PDFs

**Why Clips Matter for Designers:**
• **No more lost product links** - Everything in one place
• **Easy client presentations** - Share via Drawing Room
• **Direct flow to estimates** - Auto-populate pricing
• **Complete specification tracking** - SpecNum system
• **Vendor management** - Track where to order

**The Chrome Extension** lets you clip products from ANY website with one click while browsing vendor sites.`,
        highlightAreas: [
          { x: 160, y: 180, width: 1000, height: 500, label: 'Clip Library Grid' }
        ],
        duration: 12000,
      },
      {
        id: 'clips-2',
        title: 'Organizing Your Clips',
        screenshot: '/tutorials/clips/01-clips-library.png',
        narration: `The **Clip Library** displays all your saved products with powerful filtering.

**Filter Options:**
🔍 **Search** - Find clips by name, vendor, or notes
📁 **By Project** - Show clips for a specific project
🏷️ **By Product Type** - Lighting, Furniture, Hardware, etc.
🔖 **By Tags** - Custom tags you've created

**View Options:**
📊 **Grid View** - Visual cards with images (default)
📋 **List View** - Compact table format

**Clip Status Indicators:**
• **"In Project"** - Assigned to a project
• **PDF Count** - Number of spec sheets attached
• **Price** - Current pricing when available

**Organizing Best Practices:**
1. Always assign clips to a project
2. Use consistent naming conventions
3. Attach manufacturer spec sheets
4. Add notes about why you selected each item
5. Tag with custom categories for easy filtering

**Pro Tip:** Use the "Select" mode to bulk-assign clips to projects or export spec sheets.`,
        highlightAreas: [
          { x: 160, y: 90, width: 400, height: 50, label: 'Search & Filters' },
          { x: 540, y: 120, width: 350, height: 50, label: 'Filter Dropdowns' }
        ],
        cursorPath: [
          { x: 350, y: 140, delay: 0 },
          { x: 600, y: 140, delay: 1000 },
          { x: 850, y: 140, delay: 2000 },
        ],
        duration: 12000,
      },
      {
        id: 'clips-3',
        title: 'The Clip Workflow',
        screenshot: '/tutorials/clips/01-clips-library.png',
        narration: `Understanding the **complete clip workflow** is essential for efficient project management.

**The Journey of a Clip:**

1️⃣ **SAVE** - Clip a product from a vendor website
   → Appears in Clip Library as "Unassigned"
   → Chrome Extension captures image, price, URL

2️⃣ **ASSIGN** - Add the clip to a project and room
   → Now organized by project
   → Visible in Project Hub > Clips tab

3️⃣ **SPECIFY** - Add detailed specifications
   → Attach manufacturer spec sheets (PDFs)
   → Enter custom notes and requirements
   → Assign a Spec Category (ELE, PLM, etc.)

4️⃣ **PRESENT** - Share with client via Drawing Room
   → Client can view and comment
   → No pricing shown to clients (that's internal)

5️⃣ **APPROVE** - Client approves the selection
   → Clip receives a SpecNum (SK001, SK002, etc.)
   → Ready for procurement

6️⃣ **PROCURE** - Add to estimate or purchase order
   → Pricing flows into financial documents
   → Track order status

**The SpecNum System:**
Once approved, clips get sequential numbers like SK001, SK002. These appear on ALL documents - making it easy to reference items across contracts, POs, and communications with contractors.`,
        duration: 15000,
      },
      {
        id: 'clips-4',
        title: 'Export Specification Sheets',
        screenshot: '/tutorials/clips/01-clips-library.png',
        narration: `KEY-MADE can generate professional **Specification Sheets** from your clips.

**Three Types of Spec Sheets:**

📋 **Client Sheet**
• Professional presentation with images
• QR codes linking to product details
• **No pricing visible** - appropriate for client review
• Perfect for Drawing Room sharing

🔧 **Trades Sheet**
• Technical specs with QR codes
• Links to manufacturer PDFs
• Organized by Spec Category (Electrical, Plumbing, etc.)
• For contractors and installers

💰 **Internal Sheet**
• Complete cost breakdown
• Markup and vendor information
• For internal use only
• Includes purchase URLs

**How to Export:**
1. Go to Project Hub > Clips tab
2. Select the clips you want to include
3. Click the appropriate "Download" button
4. PDF generates automatically

**When to Use Each:**
• **Client Sheet** → Send to client for review
• **Trades Sheet** → Send to contractors for installation
• **Internal Sheet** → Keep for your own records

**Pro Tip:** The QR codes on spec sheets link directly to manufacturer documentation - contractors can scan them on-site!`,
        highlightAreas: [
          { x: 320, y: 250, width: 800, height: 150, label: 'Export Options' }
        ],
        duration: 12000,
      },
    ]
  },

  'financials-mastery': {
    title: 'Financial Management',
    description: 'Estimates, invoices, and getting paid',
    duration: '20 minutes', 
    steps: [
      {
        id: 'fin-1',
        title: 'Back of House Overview',
        screenshot: '/tutorials/estimates/01-financials-overview.png',
        narration: `The **Back of House** is your financial command center.

**Dashboard Metrics:**

💰 **Total Revenue** - All income across projects
📊 **Total Expenses** - Money spent on materials, contractors
📈 **Net Profit** - Your bottom line with margin percentage
📁 **Active Projects** - Projects currently in progress

**Dashboard Views:**

📊 **Overview Tab:**
• Cash Flow chart (Income vs. Expenses over time)
• Quick Stats (Pending Invoices, Overdue, Paid)
• Recent Transactions
• Projects Needing Attention

📁 **Projects Tab:**
• Financial summary per project
• Budget vs. actual tracking
• Profitability analysis

💳 **Expenses Tab:**
• Expense tracking and categories
• Receipt uploads
• Vendor payments

**Quick Action Buttons:**
• **Budget Tracker** - Detailed budget analysis
• **Invoices** - Manage client billing
• **Change Orders** - Track scope changes
• **RFPs & POs** - Procurement management

**Pro Tip:** Check "Needs Attention" daily - projects over 80% budget require immediate review.`,
        highlightAreas: [
          { x: 160, y: 85, width: 900, height: 120, label: 'Financial Summary Cards' },
          { x: 920, y: 280, width: 350, height: 300, label: 'Quick Stats' }
        ],
        duration: 12000,
      },
      {
        id: 'fin-2',
        title: 'Creating Estimates',
        screenshot: '/tutorials/estimates/01-financials-overview.png',
        narration: `**Estimates** are detailed cost proposals you send to clients.

**Ways to Create an Estimate:**

📋 **Manual Entry:**
• Type product names and descriptions
• Enter quantities and unit prices
• Add specifications manually

✂️ **Import from Clips:**
• Select approved clips from the project
• Pricing auto-fills from clip data
• Organize by room automatically

🔄 **Auto-Fill from Project:**
• Pulls ALL approved clips
• Imports rooms from TakeOff
• One-click estimate creation!

**Estimate Components:**

📍 **Locations** - Rooms or areas (Kitchen, Master Bath)
📁 **Categories** - Types of work (Cabinetry, Lighting)
📦 **Items** - Individual products or services

**Each Item Contains:**
• Description and specifications
• Quantity and unit of measure
• Material costs
• Labor costs (hours × rate)
• Subcontractor costs
• Markup percentage

**Estimate to Contract Flow:**
1. Create estimate with all items
2. Client reviews and approves
3. Generate contract from estimate
4. Send for signature
5. Convert to invoice for billing`,
        duration: 15000,
      },
      {
        id: 'fin-3',
        title: 'Invoice Management',
        screenshot: '/tutorials/estimates/01-financials-overview.png',
        narration: `**Invoices** turn your estimates into client bills.

**Invoice Summary Cards:**
• **Total Invoiced** - Sum of all invoices sent
• **Total Paid** - Payments received
• **Outstanding** - Amounts still owed
• **Invoice Count** - Number of invoices

**Creating Invoices:**

📄 **From Estimate:**
• Click "Create Invoice" on approved estimate
• All line items transfer automatically
• Maintains formatting and pricing

📝 **Manual Invoice:**
• Click "+ New Invoice"
• Select project and client
• Add line items manually

**Invoice Status Workflow:**
1. **Draft** - Still editing
2. **Sent** - Delivered to client
3. **Viewed** - Client opened it
4. **Paid** - Payment received
5. **Overdue** - Past due date

**Payment Options:**
• Record partial payments
• Track payment dates
• Add late fees if needed

**Accounting Integration:**
Connect to **Xero** or **QuickBooks** to:
• Auto-sync invoices
• Create contacts automatically
• Track payments in real-time

**Pro Tip:** Click "Connect Accounting" to link your accounting software and eliminate double data entry!`,
        highlightAreas: [
          { x: 830, y: 20, width: 130, height: 45, label: 'Connect Accounting' }
        ],
        duration: 12000,
      },
      {
        id: 'fin-4',
        title: 'RFPs & Purchase Orders',
        screenshot: '/tutorials/estimates/01-financials-overview.png',
        narration: `**RFPs (Request for Proposals)** help you get competitive vendor quotes.

**RFP Workflow:**

1️⃣ **Create RFP**
• Select items from your estimate
• Set due date for responses
• Add scope requirements

2️⃣ **Send to Vendors**
• Choose contractors to receive RFP
• Each gets a unique portal link
• Track who's viewed it

3️⃣ **Receive Bids**
• Vendors submit through portal
• Compare quotes side-by-side
• Review labor vs. material breakdown

4️⃣ **Award Contract**
• Select winning bid
• Auto-generate Purchase Order
• Notify awarded contractor

**Purchase Order Features:**
• Sequential numbering (PO-2026-001)
• Status tracking: Draft → Issued → Confirmed → Shipped → Received
• Partial shipment recording
• Item-by-item receipt tracking

**Materials List View:**
• Consolidated view of ALL project materials
• Group by Room, Vendor, Category, or Status
• Track ordered vs. received quantities
• Export for purchasing

**Pro Tip:** Always get at least 3 quotes for major purchases - the RFP system makes comparison easy!`,
        duration: 15000,
      },
    ]
  },

  'contractor-management': {
    title: 'Managing Contractors',
    description: 'Build and organize your contractor network',
    duration: '12 minutes',
    steps: [
      {
        id: 'contractor-1',
        title: 'Your Contractor Network',
        screenshot: '/tutorials/contractors/01-contractors-overview.png',
        narration: `The **Contractor Management** system helps you build and maintain your professional network.

**Contractor Types:**

🏗️ **General Contractors (GC)**
• Oversee entire projects
• Can have sub-contractors assigned
• Primary construction contact

🔧 **Subcontractors (Sub)**
• Specialized trades
• Work under GC or directly with you
• Examples: Electricians, Plumbers, Painters

🔩 **Sub-subs**
• Work under a specific GC
• Hired by the GC, not you directly

**Summary Cards:**
• **Total** - All contractors in your network
• **GCs** - General Contractors count
• **Direct Subs** - Subs you hire directly
• **Pending** - New contractors awaiting review
• **Expiring** - Insurance expiring soon

**Quick Actions:**
• **+ Sub-sub** - Add a sub to a GC
• **Assign** - Assign to a project
• **Doc** - View/upload documents

**Document Management:**
Track for each contractor:
• Insurance certificates (liability, workers comp)
• W-9 forms
• License information
• Contract agreements`,
        highlightAreas: [
          { x: 270, y: 75, width: 900, height: 80, label: 'Summary Cards' },
          { x: 270, y: 155, width: 400, height: 45, label: 'Filter Tabs' }
        ],
        duration: 12000,
      },
      {
        id: 'contractor-2',
        title: 'Adding Contractors',
        screenshot: '/tutorials/contractors/01-contractors-overview.png',
        narration: `Let's walk through **adding a new contractor** to your network.

**Click "+ Add Contractor"** to open the form.

**Required Information:**
• **Company Name** - Their business name
• **Contact Name** - Primary point of contact
• **Email** - For communications and portal access
• **Phone** - Direct phone number
• **Specialty** - Electrical, Plumbing, Carpentry, etc.
• **Type** - GC, Sub, or Sub-sub

**Optional but Recommended:**
• License number
• Insurance coverage amounts
• Insurance expiration dates
• Notes about past work quality

**Bulk Import:**
Have lots of contractors to add?
• Click **"Import CSV"**
• Download the template
• Fill in your contractor data
• Upload and review before saving

**Specialty Categories:**
KEY-MADE has pre-defined specialties:
• Electrical
• Plumbing
• HVAC
• Carpentry
• Millwork
• Painting
• Flooring
• And many more...

You can add custom specialties in Admin Settings.`,
        cursorPath: [
          { x: 1100, y: 28, delay: 0 },
        ],
        clickTarget: { x: 1100, y: 28, label: 'Click "Add Contractor"' },
        duration: 12000,
      },
      {
        id: 'contractor-3',
        title: 'Contractor Portal Access',
        screenshot: '/tutorials/contractors/01-contractors-overview.png',
        narration: `Each contractor can access a **Contractor Portal** for collaboration.

**What Contractors See in Their Portal:**

📋 **RFPs Tab**
• RFPs you've sent them
• Ability to submit bids
• View project requirements

📄 **Spec Sheets Tab**
• Product specifications for their trade
• Manufacturer PDFs
• Technical requirements

💬 **Messages Tab**
• Direct communication with your team
• Project-specific discussions
• Timestamped message history

📝 **Progress Log Tab**
• Submit daily/weekly progress updates
• Upload site photos
• Report issues or delays

**Creating Portal Access:**
1. Click the **⋮** menu on a contractor card
2. Select **"Create Portal Access"**
3. Copy the unique link
4. Send to the contractor

**Portal Permissions:**
Control what contractors can see:
• View project documents ✓/✗
• Submit bids ✓/✗
• View other bids ✓/✗
• Download attachments ✓/✗

**Pro Tip:** Use the portal for all contractor communications - everything is documented and timestamped!`,
        duration: 12000,
      },
    ]
  },

  'rfp-workflow': {
    title: 'RFP & Procurement Workflow',
    description: 'Request quotes and manage purchase orders',
    duration: '15 minutes',
    steps: [
      {
        id: 'rfp-1',
        title: 'RFPs & Purchase Orders Overview',
        screenshot: '/tutorials/estimates/01-financials-overview.png',
        narration: `The **RFPs & Purchase Orders** page manages your entire procurement workflow.

**Summary Cards:**
• **Total RFPs** - All RFPs created
• **Awaiting Response** - Waiting for vendor bids
• **Purchase Orders** - Issued POs
• **Total PO Value** - Sum of all PO amounts

**Three Tabs:**

📋 **RFPs Tab**
• List of all Requests for Proposals
• Status tracking (Draft, Sent, Received)
• Due dates and vendor counts

🛒 **Purchase Orders Tab**
• All purchase orders
• Status tracking (Draft, Issued, Shipped, Received)
• Order totals and delivery status

📦 **Materials List Tab**
• Consolidated view of project materials
• Group by vendor, room, or category
• Track what's ordered vs. received

**RFP Status Workflow:**
1. **Draft** - Still editing
2. **Sent** - Delivered to contractors
3. **Received** - At least one bid submitted
4. **Awarded** - Winner selected, PO created

**Creating RFPs:**
Click **"+ New RFP"** to start the procurement process.`,
        highlightAreas: [
          { x: 270, y: 85, width: 900, height: 80, label: 'Summary Cards' },
          { x: 270, y: 170, width: 350, height: 45, label: 'Tab Navigation' }
        ],
        duration: 12000,
      },
      {
        id: 'rfp-2',
        title: 'Creating and Sending RFPs',
        screenshot: '/tutorials/estimates/01-financials-overview.png',
        narration: `Let's walk through **creating and sending an RFP**.

**Step 1: Start from Estimate**
• Open your project estimate
• Select items to include in RFP
• Click "Generate RFP"

**Step 2: Configure RFP Details**
• **Title** - Clear description of scope
• **Due Date** - Deadline for bids
• **Scope Summary** - Project overview for contractors

**Step 3: Select Contractors**
• Choose from your contractor network
• Filter by specialty (Electrical, Plumbing, etc.)
• Add multiple contractors to compare quotes

**Step 4: Review & Send**
• Preview the RFP
• Verify all items and quantities
• Click "Send to Contractors"

**What Contractors Receive:**
• Email notification with portal link
• List of items requiring quotes
• Project specifications
• Due date for response

**Tracking Responses:**
• Status updates as contractors view RFP
• Notification when bids are submitted
• Side-by-side comparison of quotes

**Pro Tip:** Include clear scope descriptions - vague RFPs get vague bids!`,
        duration: 12000,
      },
      {
        id: 'rfp-3',
        title: 'Comparing Bids & Awarding',
        screenshot: '/tutorials/estimates/01-financials-overview.png',
        narration: `Once contractors submit bids, you can **compare and award** the work.

**Viewing Submitted Bids:**
• Click on any RFP to see details
• View all bids in a comparison table
• See breakdowns: Labor, Materials, Total

**Bid Comparison Factors:**
💰 **Price** - Total bid amount
📅 **Timeline** - Estimated duration
🗓️ **Availability** - When they can start
📝 **Notes** - Special conditions or exclusions

**Questions to Consider:**
• Is the lowest bid actually the best value?
• Does the timeline work with your schedule?
• What's their track record on similar projects?
• Are there hidden costs in the notes?

**Awarding the Bid:**
1. Select the winning contractor
2. Click "Award Bid"
3. System automatically creates a Purchase Order
4. Contractor is notified

**After Awarding:**
• Other contractors receive "not selected" notification
• PO is ready for your review and issuance
• Work scope is locked in

**Pro Tip:** Sometimes the mid-range bid is best - too cheap often means cut corners, too expensive doesn't mean better quality.`,
        duration: 12000,
      },
    ]
  },

  'change-orders': {
    title: 'Managing Change Orders',
    description: 'Handle scope changes professionally',
    duration: '10 minutes',
    steps: [
      {
        id: 'co-1',
        title: 'What Are Change Orders?',
        screenshot: '/tutorials/estimates/01-financials-overview.png',
        narration: `**Change Orders** document modifications to the original project scope.

**Why Change Orders Matter:**
• Protects you legally
• Documents client approval
• Tracks cost impacts
• Maintains project records

**Common Reasons for Change Orders:**
• Client requests additions or upgrades
• Unforeseen site conditions
• Material substitutions
• Design modifications
• Timeline changes

**Change Order Summary Cards:**
• **Total** - All change orders
• **Pending** - Awaiting approval
• **Approved** - Client has approved
• **Cost Impact** - Net change to budget

**Each Change Order Contains:**
• **CO Number** - Sequential ID (CO-01191526)
• **Status** - Draft, Pending, Approved
• **Title** - What's changing
• **Project** - Which project
• **Description** - Detailed explanation
• **Cost Impact** - Additional cost or credit
• **Timeline Impact** - Days added/removed
• **Reason** - Why the change is needed

**Golden Rule:** NEVER do additional work without a signed change order!`,
        highlightAreas: [
          { x: 260, y: 85, width: 900, height: 85, label: 'Change Order Summary' }
        ],
        duration: 12000,
      },
      {
        id: 'co-2',
        title: 'Creating Change Orders',
        screenshot: '/tutorials/estimates/01-financials-overview.png',
        narration: `Let's walk through **creating a change order**.

**Click "+ New Change Order"**

**Step 1: Basic Information**
• **Title** - Clear description (e.g., "Additional Lighting - Kitchen")
• **Project** - Select from dropdown
• **Reason** - Client Request, Site Condition, Design Change, etc.

**Step 2: Cost Impact**
• Calculate additional costs:
  - Materials
  - Labor hours
  - Subcontractor fees
  - Design fees
• Or enter a credit if reducing scope

**Step 3: Timeline Impact**
• Estimate additional days needed
• Note if it affects the critical path

**Step 4: Description**
• Detail exactly what's changing
• Reference original scope vs. new scope
• List any excluded items

**Before Sending to Client:**
• Review all costs carefully
• Verify timeline impact
• Attach any supporting documents
• Get internal approval if required

**Pro Tip:** Be specific! "Add recessed lights" is vague. "Add 6 Lithonia 6-inch LED recessed lights in kitchen ceiling per attached layout" is professional.`,
        cursorPath: [
          { x: 1100, y: 31, delay: 0 },
        ],
        clickTarget: { x: 1100, y: 31, label: 'Click "New Change Order"' },
        duration: 12000,
      },
      {
        id: 'co-3',
        title: 'Change Order Approval Workflow',
        screenshot: '/tutorials/estimates/01-financials-overview.png',
        narration: `**Getting change orders approved** protects your business.

**Approval Workflow:**

1️⃣ **Draft**
• You create the change order
• Review and edit as needed
• Internal team approval (if required)

2️⃣ **Pending Approval**
• Send to client for review
• Client can view in Drawing Room
• Track when they open it

3️⃣ **Approved**
• Client signs off
• Budget automatically updated
• Work can proceed

4️⃣ **Rejected** (if applicable)
• Client declines change
• Document the rejection
• Original scope continues

**Client Communication:**
• Send clear explanation with CO
• Explain WHY the change is needed
• Show cost breakdown
• Note timeline impact

**Linking to Financials:**
Approved change orders automatically:
• Update project budget
• Add to estimate/invoice
• Track in financial reports

**Legal Protection:**
• Always get written approval
• Keep copies of all communications
• Document verbal approvals in writing

**Pro Tip:** Send change orders as soon as scope creep appears - don't wait until the end of the project!`,
        duration: 12000,
      },
    ]
  },

  'drawing-room': {
    title: 'Client Portal (Drawing Room)',
    description: 'Share designs and get client approvals',
    duration: '12 minutes',
    steps: [
      {
        id: 'dr-1',
        title: 'What is the Drawing Room?',
        screenshot: '/tutorials/welcome/01-dashboard-overview.png',
        narration: `The **Drawing Room** is your client's dedicated portal for project collaboration.

**What Clients See:**

🖼️ **Welcome Tab**
• Featured drawing/plan prominently displayed
• Project overview
• Your company branding

💡 **Inspiration Tab**
• Client-provided inspiration images
• Links they've shared
• Reference materials

✂️ **Selections Tab**
• Products you've selected for their project
• Images and specifications
• **No pricing shown** (that's internal)
• Approve/Reject buttons

📄 **Documents Tab**
• Contracts and agreements
• Design presentations
• Documents requiring signature

📐 **Drawings Tab**
• Floor plans and elevations
• Annotatable PDFs
• Click to view full-screen

💬 **Messages Tab**
• Direct communication
• Project discussions
• File attachments

**What Clients DON'T See:**
❌ Pricing and costs
❌ Vendor information
❌ Your internal notes
❌ Markup percentages

**This separation protects your business** while giving clients professional access to review and approve.`,
        duration: 12000,
      },
      {
        id: 'dr-2',
        title: 'Sharing Access with Clients',
        screenshot: '/tutorials/welcome/01-dashboard-overview.png',
        narration: `Let's set up **Drawing Room access** for a client.

**From Project Hub:**
1. Go to the **Portals** tab
2. Click **"Generate Drawing Room Link"**
3. Copy the unique URL
4. Send to your client

**Access Options:**
• **Public Link** - Anyone with the link can view
• **Password Protected** - Requires password to enter
• **Email Verified** - Client must verify email first

**Branding Your Portal:**
The Drawing Room shows:
• Your company logo
• Company name and tagline
• Custom color scheme (from Company Settings)
• Your contact information

**What to Include:**
Before sharing, make sure you've added:
✓ At least one floor plan/drawing
✓ Selected products (clips)
✓ Welcome message
✓ Any documents for review

**Client Experience:**
Clients receive a clean, professional portal that feels like YOUR branded tool - not a generic software product.

**Pro Tip:** Send a brief email explaining what they'll find in the Drawing Room and what actions you need from them.`,
        duration: 12000,
      },
      {
        id: 'dr-3',
        title: 'Getting Client Approvals',
        screenshot: '/tutorials/welcome/01-dashboard-overview.png',
        narration: `The Drawing Room streamlines **client approvals**.

**Product Selection Approvals:**

📋 **Selections Tab Workflow:**
1. You add clips/products to the project
2. Client views them in Selections tab
3. Each item has ✓ Approve or ✗ Reject buttons
4. Client leaves comments if needed
5. You receive notifications of decisions

**What Happens When Approved:**
• Item gets a SpecNum (SK001, SK002...)
• Moves to "Approved" status
• Ready for procurement
• Appears on all official documents

**What Happens When Rejected:**
• Item flagged for your review
• Client comments visible
• You can propose alternatives
• Resubmit for approval

**Document Approvals:**
• Upload contracts to Documents tab
• Client can view and annotate
• Digital signature capture
• Timestamped approval records

**Tracking Approval Status:**
In Project Hub > Clips:
• See which items are approved
• Filter by approval status
• Export approved items only

**Pro Tip:** Set expectations with clients about approval timelines - delays here delay the entire project!`,
        duration: 12000,
      },
    ]
  },
};

// Animated Cursor Component
const AnimatedCursor = ({ x, y, clicking, visible }) => {
  if (!visible) return null;
  
  return (
    <div 
      className="absolute pointer-events-none z-50 transition-all duration-300 ease-out"
      style={{ left: x, top: y, transform: 'translate(-2px, -2px)' }}
    >
      <MousePointer2 
        className={`w-8 h-8 text-[#70170F] drop-shadow-lg transition-transform ${clicking ? 'scale-90' : 'scale-100'}`}
        fill={clicking ? '#70170F' : 'white'}
        strokeWidth={2}
      />
      {clicking && (
        <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-[#70170F]/30 animate-ping" />
      )}
    </div>
  );
};

// Click Target Indicator
const ClickTarget = ({ x, y, label, active }) => {
  if (!active) return null;
  
  return (
    <div 
      className="absolute z-40 pointer-events-none"
      style={{ left: x - 20, top: y - 20 }}
    >
      <div className="w-10 h-10 rounded-full border-4 border-[#70170F] animate-pulse" />
      <div className="w-10 h-10 rounded-full border-2 border-[#70170F]/50 absolute top-0 left-0 animate-ping" />
      {label && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 bg-[#70170F] text-white text-xs px-3 py-1 rounded-full whitespace-nowrap shadow-lg">
          {label}
        </div>
      )}
    </div>
  );
};

// Highlight Area Component
const HighlightArea = ({ x, y, width, height, label, active }) => {
  if (!active) return null;
  
  return (
    <div 
      className="absolute border-2 border-[#70170F] rounded-lg bg-[#70170F]/10 z-30 pointer-events-none transition-all duration-500"
      style={{ left: `${x}px`, top: `${y}px`, width: `${width}px`, height: `${height}px` }}
    >
      {label && (
        <div className="absolute -top-6 left-2 bg-[#70170F] text-white text-xs px-2 py-0.5 rounded">
          {label}
        </div>
      )}
    </div>
  );
};

// Main Interactive Tutorial Component
const InteractiveTutorial = ({ tutorialId, onClose, onComplete, startingRoute }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isCursorVisible, setIsCursorVisible] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);
  const [showClickTarget, setShowClickTarget] = useState(false);
  const containerRef = useRef(null);
  
  const tutorial = INTERACTIVE_TUTORIALS[tutorialId];
  const steps = tutorial?.steps || [];
  const currentStepData = steps[currentStep] || {};
  const totalSteps = steps.length;
  const progress = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  // Animate cursor along path
  useEffect(() => {
    if (!tutorial || !isPlaying || !currentStepData?.cursorPath) return;
    
    const path = currentStepData.cursorPath;
    let pathIndex = 0;
    
    setIsCursorVisible(true);
    setCursorPosition(path[0]);
    
    const animateCursor = () => {
      if (pathIndex < path.length - 1) {
        pathIndex++;
        setCursorPosition(path[pathIndex]);
        setTimeout(animateCursor, path[pathIndex].delay || 500);
      } else if (currentStepData.clickTarget) {
        setCursorPosition(currentStepData.clickTarget);
        setTimeout(() => {
          setIsClicking(true);
          setShowClickTarget(true);
          setTimeout(() => setIsClicking(false), 200);
        }, 500);
      }
    };
    
    const timeout = setTimeout(animateCursor, 1000);
    return () => clearTimeout(timeout);
  }, [tutorial, currentStep, isPlaying, currentStepData]);

  // Show highlights after a delay
  useEffect(() => {
    if (!tutorial) return;
    setShowHighlights(false);
    const timeout = setTimeout(() => setShowHighlights(true), 500);
    return () => clearTimeout(timeout);
  }, [tutorial, currentStep]);

  const goNext = useCallback(() => {
    setShowClickTarget(false);
    setIsCursorVisible(false);
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onComplete?.();
      if (startingRoute === '/blueprints') {
        navigate('/blueprints');
      }
      onClose();
    }
  }, [currentStep, totalSteps, onComplete, startingRoute, navigate, onClose]);

  const goPrev = useCallback(() => {
    setShowClickTarget(false);
    setIsCursorVisible(false);
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  // Early return AFTER all hooks
  if (!tutorial) {
    return (
      <div className="fixed inset-0 z-[99999] bg-black/90 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 max-w-md text-center">
          <p className="text-gray-600 mb-4">Tutorial "{tutorialId}" not found.</p>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  // Parse markdown-like content
  const renderNarration = (text) => {
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      // Bold text
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#70170F]">$1</strong>');
      
      // Emoji bullets
      if (/^[📁✂️👥👷📄💰📚⚙️📷💲🏭📐🏠📝🏗️🔧📋➕📅📊🗺️🎨🚪1️⃣2️⃣3️⃣4️⃣5️⃣6️⃣7️⃣✅❌🔍📦💬🛠️💳📈🔖💡🖼️🗓️📍🔩📊📉]/.test(line.trim())) {
        return <p key={idx} className="ml-2 my-1.5" dangerouslySetInnerHTML={{ __html: line }} />;
      }
      
      // Regular bullets
      if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
        return <p key={idx} className="ml-6 my-1" dangerouslySetInnerHTML={{ __html: line }} />;
      }
      
      // Empty lines
      if (line.trim() === '') {
        return <div key={idx} className="h-2" />;
      }
      
      return <p key={idx} className="my-1" dangerouslySetInnerHTML={{ __html: line }} />;
    });
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-black/95 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#70170F] to-[#8B1E14] px-6 py-4 text-white flex-shrink-0">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Target className="w-6 h-6" />
              <div>
                <h2 className="font-bold text-lg">{tutorial.title}</h2>
                <p className="text-white/80 text-sm">{tutorial.description}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="bg-white/20 text-white">
              Step {currentStep + 1} of {totalSteps}
            </Badge>
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="max-w-7xl mx-auto mt-3">
          <div className="h-1 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Screenshot Area */}
        <div className="flex-1 relative bg-gray-900 flex items-center justify-center p-4" ref={containerRef}>
          <div className="relative max-w-5xl w-full aspect-video bg-gray-800 rounded-lg overflow-hidden shadow-2xl">
            {/* Screenshot */}
            <img 
              src={currentStepData.screenshot}
              alt={currentStepData.title}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 1080"><rect fill="%23374151" width="1920" height="1080"/><text x="960" y="540" fill="%239CA3AF" font-size="32" text-anchor="middle">Screenshot Loading...</text></svg>';
              }}
            />
            
            {/* Highlight Areas */}
            {currentStepData.highlightAreas?.map((area, idx) => (
              <HighlightArea key={idx} {...area} active={showHighlights} />
            ))}
            
            {/* Click Target */}
            {currentStepData.clickTarget && (
              <ClickTarget {...currentStepData.clickTarget} active={showClickTarget} />
            )}
            
            {/* Animated Cursor */}
            <AnimatedCursor 
              x={cursorPosition.x} 
              y={cursorPosition.y} 
              clicking={isClicking}
              visible={isCursorVisible}
            />
          </div>
          
          {/* Step indicator dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setShowClickTarget(false);
                  setIsCursorVisible(false);
                  setCurrentStep(idx);
                }}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentStep 
                    ? 'bg-[#70170F] scale-125' 
                    : idx < currentStep 
                      ? 'bg-green-500' 
                      : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
        
        {/* Narration Panel */}
        <div className="w-[400px] bg-white dark:bg-gray-900 flex flex-col">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#70170F] text-white flex items-center justify-center text-sm font-bold">
                {currentStep + 1}
              </span>
              {currentStepData.title}
            </h3>
          </div>
          
          <ScrollArea className="flex-1 p-6">
            <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed">
              {renderNarration(currentStepData.narration)}
            </div>
          </ScrollArea>
          
          {/* Navigation */}
          <div className="p-4 pb-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between mb-8">
            <Button
              variant="outline"
              onClick={goPrev}
              disabled={currentStep === 0}
              data-testid="tutorial-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            
            <Button
              onClick={goNext}
              className="bg-[#70170F] hover:bg-[#8B1E14] text-white"
              data-testid="tutorial-next-btn"
            >
              {currentStep === totalSteps - 1 ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Complete
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { InteractiveTutorial, INTERACTIVE_TUTORIALS };
export default InteractiveTutorial;
